import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PasswordStrengthBar = ({ password }) => {
  const getStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: 'transparent' };
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    const levels = [
      { score: 0, label: '', color: 'transparent' },
      { score: 1, label: 'Svakt', color: '#EF4444' },
      { score: 2, label: 'Middels', color: '#F59E0B' },
      { score: 3, label: 'Bra', color: '#10B981' },
      { score: 4, label: 'Sterkt', color: '#10B981' },
    ];
    return levels?.[score];
  };
  const strength = getStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
        {[1, 2, 3, 4]?.map(i => (
          <div
            key={i}
            style={{
              flex: 1,
              height: '3px',
              borderRadius: '1px',
              background: i <= strength?.score ? strength?.color : 'rgba(156, 163, 175, 0.2)',
              transition: 'background 250ms ease-out',
            }}
          />
        ))}
      </div>
      {strength?.label && (
        <p style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 500, color: strength?.color, letterSpacing: '0.04em' }}>
          Passordstyrke: {strength?.label}
        </p>
      )}
    </div>
  );
};

const InputField = ({ id, name, type = 'text', label, placeholder, value, onChange, onBlur, error, autoComplete, required }) => (
  <div style={{ marginBottom: '24px' }}>
    <label
      htmlFor={id}
      style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', display: 'block', marginBottom: '8px' }}
    >
      {label}{required && ' *'}
    </label>
    <input
      id={id}
      name={name}
      type={type}
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      style={{
        width: '100%',
        background: 'transparent',
        border: 'none',
        borderBottom: error ? '2px solid #FF5722' : '2px solid rgba(156, 163, 175, 0.3)',
        borderRadius: 0,
        padding: '10px 0',
        fontFamily: 'var(--font-body)',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.5,
        color: 'var(--color-foreground)',
        outline: 'none',
        transition: 'border-color 250ms ease-out',
        letterSpacing: '0.01em',
      }}
      onFocus={e => { if (!error) e.target.style.borderBottomColor = 'var(--color-primary)'; }}
    />
    {error && (
      <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 400, color: '#FF5722', marginTop: '6px', letterSpacing: '0.01em', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span>⚠</span>{error}
      </p>
    )}
  </div>
);

const RegisterForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return !value?.trim() ? 'Fullt navn er påkrevd' : '';
      case 'email':
        if (!value) return 'E-postadresse er påkrevd';
        if (!EMAIL_REGEX?.test(value)) return 'Ugyldig e-postadresse (f.eks. navn@eksempel.no)';
        return '';
      case 'password':
        if (!value) return 'Passord er påkrevd';
        if (value?.length < 6) return 'Passord må være minst 6 tegn';
        if (value?.length < 8) return 'Anbefalt: minst 8 tegn for sterkere passord';
        return '';
      case 'confirmPassword':
        if (!value) return 'Bekreft passord er påkrevd';
        if (formData?.password !== value) return 'Passordene stemmer ikke overens';
        return '';
      default:
        return '';
    }
  };

  const validate = () => {
    const newErrors = {};
    ['name', 'email', 'password', 'confirmPassword']?.forEach(field => {
      const err = validateField(field, formData?.[field]);
      if (err) newErrors[field] = err;
    });
    if (!formData?.acceptTerms) newErrors.acceptTerms = 'Du må godta vilkårene for å fortsette';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (touched?.[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, newValue) }));
    }
    // Re-validate confirmPassword when password changes
    if (name === 'password' && touched?.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: formData?.confirmPassword && newValue !== formData?.confirmPassword
          ? 'Passordene stemmer ikke overens' : '',
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({ name: true, email: true, password: true, confirmPassword: true, acceptTerms: true });
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    const users = JSON.parse(localStorage.getItem('veiassistent_users') || '[]');
    const existingUser = users?.find(u => u?.email === formData?.email);
    if (existingUser) {
      setErrors({ email: 'En konto med denne e-postadressen finnes allerede' });
      setIsLoading(false);
      return;
    }
    const newUser = {
      id: `user_${Date.now()}`,
      name: formData?.name?.trim(),
      email: formData?.email,
      password: formData?.password,
      accountTier: 'free',
      createdAt: new Date()?.toISOString(),
    };
    users?.push(newUser);
    localStorage.setItem('veiassistent_users', JSON.stringify(users));
    const session = { id: newUser?.id, email: newUser?.email, name: newUser?.name, accountTier: 'free', loggedInAt: new Date()?.toISOString() };
    localStorage.setItem('veiassistent_session', JSON.stringify(session));
    setIsLoading(false);
    setSuccessMessage('Konto opprettet! Omdirigerer...');
    setTimeout(() => navigate('/dashboard'), 1200);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="w-full">
      {successMessage && (
        <div
          className="mb-6 px-4 py-3 border-l-4"
          style={{ background: 'rgba(16, 185, 129, 0.08)', borderLeftColor: '#10B981', border: '1px solid rgba(16, 185, 129, 0.2)', borderLeft: '4px solid #10B981' }}
        >
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', fontWeight: 500, color: '#10B981', letterSpacing: '0.01em' }}>
            {successMessage}
          </p>
        </div>
      )}
      <InputField
        id="name" name="name" type="text" label="Fullt navn" required
        placeholder="Ola Nordmann" value={formData?.name}
        onChange={handleChange} onBlur={handleBlur} error={errors?.name} autoComplete="name"
      />
      <InputField
        id="email" name="email" type="email" label="E-postadresse" required
        placeholder="navn@eksempel.no" value={formData?.email}
        onChange={handleChange} onBlur={handleBlur} error={errors?.email} autoComplete="email"
      />
      {/* Password with strength */}
      <div style={{ marginBottom: '24px' }}>
        <label
          htmlFor="password"
          style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', display: 'block', marginBottom: '8px' }}
        >
          Passord *
        </label>
        <input
          id="password" name="password" type="password" autoComplete="new-password"
          value={formData?.password} onChange={handleChange} onBlur={handleBlur}
          placeholder="Minst 6 tegn"
          style={{
            width: '100%', background: 'transparent', border: 'none',
            borderBottom: errors?.password ? '2px solid #FF5722' : '2px solid rgba(156, 163, 175, 0.3)',
            borderRadius: 0, padding: '10px 0',
            fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400,
            lineHeight: 1.5, color: 'var(--color-foreground)', outline: 'none',
            transition: 'border-color 250ms ease-out', letterSpacing: '0.01em',
          }}
          onFocus={e => { if (!errors?.password) e.target.style.borderBottomColor = 'var(--color-primary)'; }}
        />
        <PasswordStrengthBar password={formData?.password} />
        {errors?.password && (
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 400, color: '#FF5722', marginTop: '6px', letterSpacing: '0.01em', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>⚠</span>{errors?.password}
          </p>
        )}
      </div>
      <InputField
        id="confirmPassword" name="confirmPassword" type="password" required
        label="Bekreft passord" placeholder="Gjenta passord"
        value={formData?.confirmPassword} onChange={handleChange} onBlur={handleBlur}
        error={errors?.confirmPassword} autoComplete="new-password"
      />
      {/* Terms */}
      <div style={{ marginBottom: '28px' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
          <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
            <input
              type="checkbox" name="acceptTerms"
              checked={formData?.acceptTerms} onChange={handleChange}
              style={{ position: 'absolute', opacity: 0, width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <div
              style={{
                width: '18px', height: '18px',
                border: `2px solid ${errors?.acceptTerms ? '#FF5722' : formData?.acceptTerms ? 'var(--color-primary)' : 'rgba(156, 163, 175, 0.4)'}`,
                borderRadius: 'var(--radius-sm)',
                background: formData?.acceptTerms ? 'var(--color-primary)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 150ms ease-out',
              }}
            >
              {formData?.acceptTerms && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 300, lineHeight: 1.6, color: 'var(--color-muted-foreground)', letterSpacing: '0.01em' }}>
            Jeg godtar{' '}
            <span style={{ color: 'var(--color-primary)', fontWeight: 500, cursor: 'pointer' }}>vilkårene for bruk</span>
            {' '}og{' '}
            <span style={{ color: 'var(--color-primary)', fontWeight: 500, cursor: 'pointer' }}>personvernerklæringen</span>
          </span>
        </label>
        {errors?.acceptTerms && (
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 400, color: '#FF5722', marginTop: '6px', letterSpacing: '0.01em', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>⚠</span>{errors?.acceptTerms}
          </p>
        )}
      </div>
      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        style={{
          width: '100%',
          background: isLoading ? 'rgba(255, 87, 34, 0.6)' : 'var(--color-accent)',
          border: '2px solid var(--color-accent)',
          borderRadius: 'var(--radius-sm)',
          padding: '14px 24px',
          fontFamily: 'var(--font-heading)',
          fontSize: '14px', fontWeight: 600,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: '#FFFFFF',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 250ms ease-out',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}
        onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.background = '#E64A19'; e.currentTarget.style.borderColor = '#E64A19'; } }}
        onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.borderColor = 'var(--color-accent)'; } }}
      >
        {isLoading ? (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            Oppretter konto...
          </>
        ) : 'Opprett konto'}
      </button>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', textAlign: 'center', marginTop: '24px', lineHeight: 1.5 }}>
        Har du allerede konto?{' '}
        <Link
          to="/login"
          style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 500, color: 'var(--color-primary)', textDecoration: 'none', transition: 'opacity 150ms ease-out' }}
          onMouseEnter={e => e.target.style.opacity = '0.75'}
          onMouseLeave={e => e.target.style.opacity = '1'}
        >
          Logg inn
        </Link>
      </p>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(156, 163, 175, 0.5); }
      `}</style>
    </form>
  );
};

export default RegisterForm;
